import { z } from 'zod';

import { CARD_COLORS, CARD_EXPERIENCES, CARD_IDS, type CardId } from './cards';

export const LocaleCodeSchema = z.enum(['en', 'ru']);
export const CardIdSchema = z.enum(CARD_IDS);
export const CardColorSchema = z.enum(CARD_COLORS);
export const CardExperienceSchema = z.enum(CARD_EXPERIENCES);

export const YouTubeVideoItemSchema = z.object({
  videoId: z.string().min(1),
  title: z.string().min(1),
  thumbnailUrl: z.string().url(),
  channelTitle: z.string().min(1),
});

export const TextHistoryMessageSchema = z.object({
  kind: z.literal('text'),
  sender: z.enum(['user', 'assistant']),
  text: z.string(),
});

export const VideoResultsHistoryMessageSchema = z.object({
  kind: z.literal('video_results'),
  sender: z.literal('assistant'),
  videos: z.array(YouTubeVideoItemSchema),
});

export const HistoryMessageSchema = z.discriminatedUnion('kind', [
  TextHistoryMessageSchema,
  VideoResultsHistoryMessageSchema,
]);

export const CardSummarySchema = z.object({
  id: CardIdSchema,
  name: z.string().min(1),
  welcomeMessage: z.string().min(1),
  color: CardColorSchema,
  experience: CardExperienceSchema,
  isAudio: z.boolean(),
});

export const AppConfigSchema = z.object({
  imageServiceBaseUrl: z.string().url(),
  youtubeAllowanceSecondsPerDay: z.number().int().positive(),
});

export const ImageUiCopySchema = z.object({
  addPhoto: z.string().min(1),
  backToCards: z.string().min(1),
  createButton: z.string().min(1),
  creatingTemplate: z.string().min(1),
  currentImageAlt: z.string().min(1),
  deleteImage: z.string().min(1),
  editButton: z.string().min(1),
  emptyTitle: z.string().min(1),
  generationError: z.string().min(1),
  imageCounterTemplate: z.string().min(1),
  newImageReady: z.string().min(1),
  nextImage: z.string().min(1),
  photoError: z.string().min(1),
  previousImage: z.string().min(1),
  promptLabelCreate: z.string().min(1),
  promptLabelEdit: z.string().min(1),
  promptPlaceholderCreate: z.string().min(1),
  promptPlaceholderEdit: z.string().min(1),
  storageWarning: z.string().min(1),
});

export const CardsResponseSchema = z.object({
  cards: z.array(CardSummarySchema),
  appConfig: AppConfigSchema,
  imageUi: ImageUiCopySchema,
});

export const ChatRequestSchema = z.object({
  cardId: CardIdSchema,
  message: z.string().trim().min(1),
  history: z.array(HistoryMessageSchema).default([]),
  lang: LocaleCodeSchema.default('en'),
});

export const MessageResponseSchema = z.object({
  kind: z.literal('message'),
  text: z.string(),
});

export const VideoSearchResponseSchema = z.object({
  kind: z.literal('video_search'),
  confirmationText: z.string().min(1),
  searchTerms: z.string().min(1),
});

export const ChatResponseSchema = z.discriminatedUnion('kind', [
  MessageResponseSchema,
  VideoSearchResponseSchema,
]);

export const RealtimeSessionRequestSchema = z.object({
  cardId: CardIdSchema,
  lang: LocaleCodeSchema.default('en'),
});

export const RealtimeSessionResponseSchema = z.object({
  clientSecret: z.string().min(1),
  model: z.string().min(1),
});

export const VideoSearchToolArgsSchema = z.object({
  search_terms: z.string().trim().min(1),
  user_confirmation_message: z.string().trim().min(1),
});

export const YouTubeSearchResponseSchema = z.object({
  videos: z.array(YouTubeVideoItemSchema),
});

const Base64ImageSchema = z.string().min(1);

export const ImageStreamPreviewSchema = z.object({
  type: z.literal('preview'),
  step: z.number().int().positive(),
  total_steps: z.number().int().positive(),
  media_type: z.literal('image/jpeg'),
  image: Base64ImageSchema,
});

export const ImageGenerationMetadataSchema = z.object({
  model: z.string().min(1),
  model_revision: z.string().min(1),
  seed: z.number().int().min(0).max(4_294_967_295),
  steps: z.number().int().min(1).max(20),
  width: z.number().int().min(256).max(2048).multipleOf(16),
  height: z.number().int().min(256).max(2048).multipleOf(16),
  quantization: z.number(),
  generation_time_seconds: z.number().nonnegative(),
}).passthrough().refine(
  ({ width, height }) => width * height <= 2_097_152,
  { message: 'Image dimensions exceed the service pixel limit.', path: ['width'] },
);

export const ImageStreamCompleteSchema = z.object({
  type: z.literal('complete'),
  media_type: z.literal('image/png'),
  image: Base64ImageSchema,
  metadata: ImageGenerationMetadataSchema,
});

export const ImageStreamErrorSchema = z.object({
  type: z.literal('error'),
  code: z.string().min(1),
  message: z.string().min(1),
});

export const ImageStreamEventSchema = z.discriminatedUnion('type', [
  ImageStreamPreviewSchema,
  ImageStreamCompleteSchema,
  ImageStreamErrorSchema,
]);

export const ApiErrorSchema = z.object({
  error: z.string().min(1),
});

export type LocaleCode = z.infer<typeof LocaleCodeSchema>;
export type { CardId };
export type AppConfig = z.infer<typeof AppConfigSchema>;
export type CardSummary = z.infer<typeof CardSummarySchema>;
export type CardsResponse = z.infer<typeof CardsResponseSchema>;
export type ImageUiCopy = z.infer<typeof ImageUiCopySchema>;
export type HistoryMessage = z.infer<typeof HistoryMessageSchema>;
export type ChatRequest = z.infer<typeof ChatRequestSchema>;
export type ChatResponse = z.infer<typeof ChatResponseSchema>;
export type RealtimeSessionRequest = z.infer<typeof RealtimeSessionRequestSchema>;
export type RealtimeSessionResponse = z.infer<typeof RealtimeSessionResponseSchema>;
export type VideoSearchToolArgs = z.infer<typeof VideoSearchToolArgsSchema>;
export type YouTubeVideoItem = z.infer<typeof YouTubeVideoItemSchema>;
export type ImageGenerationMetadata = z.infer<typeof ImageGenerationMetadataSchema>;
export type ImageStreamEvent = z.infer<typeof ImageStreamEventSchema>;
